import type { IOrganizationRepository } from "@/application/ports/repositories";
import type {
	Organization,
	UserOrganization,
	OrganizationUser,
	OrganizationTemplate,
	UpdateOrganizationRequest,
	OrganizationResponse,
} from "@/domain/organization";
import type { OrganizationInvitation } from "@/domain/invitation";
import { apiClient } from "../client";

// Raw membership shape returned by GET /users/me/memberships
interface RawMembership {
	organization: {
		uuid: string;
		name: string;
		slug: string;
		logo_signed_url?: string;
		is_active: boolean;
	};
	role: string;
	is_default: boolean;
	joined_at: string;
}

function transformMemberships(data: RawMembership[]): UserOrganization[] {
	return data.map((membership) => ({
		organization: {
			uuid: membership.organization.uuid,
			name: membership.organization.name,
			slug: membership.organization.slug,
			logo_signed_url: membership.organization.logo_signed_url,
			is_active: membership.organization.is_active,
		} as Organization,
		role: membership.role,
		is_default: membership.is_default,
		joined_at: membership.joined_at,
	}));
}

export class OrganizationRepository implements IOrganizationRepository {
	async updateOrganization(
		data: UpdateOrganizationRequest,
	): Promise<OrganizationResponse> {
		const response = await apiClient.patch<OrganizationResponse>(
			"/organizations",
			data,
		);
		return response.data;
	}

	async getOrganization(): Promise<OrganizationResponse> {
		const response =
			await apiClient.get<OrganizationResponse>("/organizations");
		return response.data;
	}

	async createOrganization(data: {
		name: string;
		slug: string;
		email: string;
		logo?: string;
	}): Promise<{ logo_signed_url?: string }> {
		const response = await apiClient.post<{ logo_signed_url?: string }>(
			"/organizations",
			data,
		);
		return response.data;
	}

	async getUserOrganizations(): Promise<UserOrganization[]> {
		const response = await apiClient.get<RawMembership[]>(
			"/users/me/memberships",
		);
		return transformMemberships(response.data);
	}

	async switchOrganization(
		organizationUuid: string | number,
	): Promise<{ access_token?: string; refresh_token?: string }> {
		const response = await apiClient.post<{
			access_token?: string;
			refresh_token?: string;
		}>("/auth/switch-organization", { organization_uuid: organizationUuid });
		return response.data;
	}

	async getOrganizationUsers(): Promise<OrganizationUser[]> {
		const response = await apiClient.get<OrganizationUser[]>("/memberships");
		return response.data;
	}

	async updateMemberRole(
		memberUuid: string,
		role: string,
	): Promise<OrganizationUser> {
		const response = await apiClient.patch<OrganizationUser>(
			`/memberships/${memberUuid}`,
			{
				role,
			},
		);
		return response.data;
	}

	async updateMemberStatus(
		memberUuid: string,
		is_active: boolean,
	): Promise<OrganizationUser> {
		const response = await apiClient.patch<OrganizationUser>(
			`/memberships/${memberUuid}`,
			{
				is_active,
			},
		);
		return response.data;
	}

	async removeMember(memberUuid: string): Promise<void> {
		await apiClient.delete(`/memberships/${memberUuid}`);
	}

	async getOrganizationInvitations(): Promise<OrganizationInvitation[]> {
		const response =
			await apiClient.get<OrganizationInvitation[]>("/invitations");
		return response.data;
	}

	async resendInvitation(invitationUuid: string): Promise<void> {
		await apiClient.post(`/invitations/${invitationUuid}/resend`);
	}

	async cancelInvitation(invitationUuid: string): Promise<void> {
		await apiClient.delete(`/invitations/${invitationUuid}`);
	}

	async getOrganizationTemplates(): Promise<OrganizationTemplate[]> {
		const response = await apiClient.get<OrganizationTemplate[]>("/templates");
		return response.data;
	}

	async updateOrganizationLogo(
		logoBase64: string,
	): Promise<{ logo_signed_url?: string }> {
		const response = await apiClient.patch<{ logo_signed_url?: string }>(
			"/organizations",
			{
				logo: logoBase64,
			},
		);
		return response.data;
	}
}
